/**
 * Die Zwischenablage des Belegeditors.
 *
 * Sie liegt **im Browser**, nicht auf dem Server — und das ist die Abgrenzung
 * zur Bausteinbibliothek: die Bibliothek ist dauerhaft, geteilt und gepflegt,
 * die Zwischenablage ist flüchtig und gehört zu dem Gerät, an dem gerade
 * gearbeitet wird. Wer etwas dauerhaft wiederverwenden will, speichert einen
 * Block; wer aus drei alten Angeboten etwas für ein neues zusammensucht,
 * nimmt die Ablage.
 *
 * Sie **sammelt an** statt zu ersetzen: man geht mehrere Belege durch und
 * nimmt aus jedem etwas mit. Geleert wird ausdrücklich.
 */
import type { WebInvoiceLine } from "./invoices";
import { newInvoiceLine } from "./invoiceEditor";
import { BLOCK_KIND } from "./invoiceBlocks";

const SPEICHER = "kontor.beleg.zwischenablage";

/** Mehr passt nicht sinnvoll in ein Panel — und `localStorage` ist begrenzt. */
const MAX_EINTRAEGE = 50;

/** Eine einzelne Position oder ein ganzer Block samt seiner Positionen. */
export type AblageEintrag = {
  /** Eigene ID der Ablage — nicht die Zeilen-ID, die wird beim Einfügen neu vergeben. */
  id: string;
  art: "position" | "block";
  /** Bei `art: "block"` der Blockkopf, bei einer Position die Zeile selbst. */
  kopf: WebInvoiceLine;
  /** Nur bei `art: "block"` gefüllt. */
  zeilen: WebInvoiceLine[];
  /** Woher der Eintrag stammt, für die Anzeige im Panel. */
  quelle: string;
  zeitpunkt: string;
};

const frischeId = () => (
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `ablage-${Date.now()}-${Math.random().toString(36).slice(2)}`
);

/**
 * Lesen ist immer defensiv: ein privates Fenster, gelöschte Site-Daten oder
 * ein Browser, der Speicher sperrt, sind kein Fehlerfall — dann ist die
 * Ablage eben leer.
 */
export function ablageLesen(): AblageEintrag[] {
  try {
    const roh = localStorage.getItem(SPEICHER);
    if (!roh) return [];
    const daten = JSON.parse(roh);
    if (!Array.isArray(daten)) return [];
    return daten.filter((eintrag): eintrag is AblageEintrag => (
      !!eintrag && typeof eintrag === "object"
      && typeof eintrag.id === "string"
      && (eintrag.art === "position" || eintrag.art === "block")
      && !!eintrag.kopf
    ));
  } catch {
    return [];
  }
}

export function ablageSchreiben(eintraege: AblageEintrag[]): AblageEintrag[] {
  const gekappt = eintraege.slice(0, MAX_EINTRAEGE);
  try {
    localStorage.setItem(SPEICHER, JSON.stringify(gekappt));
    // Das `storage`-Ereignis feuert nur in ANDEREN Tabs. Damit das Panel im
    // eigenen Tab mitbekommt, dass sich etwas geändert hat, gibt es ein
    // eigenes Ereignis — sonst müsste jede Ansicht pollen.
    window.dispatchEvent(new CustomEvent(ABLAGE_EREIGNIS));
  } catch {
    // Voller oder gesperrter Speicher: die Ablage ist eine Bequemlichkeit,
    // kein Datenspeicher. Der Beleg selbst liegt längst auf dem Server.
  }
  return gekappt;
}

export const ABLAGE_EREIGNIS = "kontor:zwischenablage";

/** Neue Einträge oben anfügen — zuletzt Kopiertes steht vorn. */
export function ablageErgaenzen(neu: AblageEintrag[]): AblageEintrag[] {
  return ablageSchreiben([...neu, ...ablageLesen()]);
}

export function ablageEntfernen(id: string): AblageEintrag[] {
  return ablageSchreiben(ablageLesen().filter((eintrag) => eintrag.id !== id));
}

export function ablageLeeren(): AblageEintrag[] {
  return ablageSchreiben([]);
}

/** Eine einzelne Position für die Ablage verpacken. */
export function eintragAusPosition(line: WebInvoiceLine, quelle: string): AblageEintrag {
  return {
    id: frischeId(), art: "position", kopf: { ...line }, zeilen: [],
    quelle, zeitpunkt: new Date().toISOString(),
  };
}

/** Einen Block mitsamt seinen Positionen für die Ablage verpacken. */
export function eintragAusBlock(
  block: WebInvoiceLine,
  zeilen: WebInvoiceLine[],
  quelle: string,
): AblageEintrag {
  return {
    id: frischeId(), art: "block", kopf: { ...block },
    zeilen: zeilen.map((line) => ({ ...line })),
    quelle, zeitpunkt: new Date().toISOString(),
  };
}

/**
 * Einträge in Belegzeilen zurückverwandeln.
 *
 * **Jede Zeile bekommt eine frische ID.** Ohne das trüge ein zweimal
 * eingefügter Eintrag dieselben IDs — React verlöre die Zuordnung beim
 * Rendern, und ein Verschieben oder Löschen träfe die falsche Zeile.
 */
export function zeilenAusEintraegen(eintraege: AblageEintrag[]): WebInvoiceLine[] {
  return eintraege.flatMap((eintrag) => {
    if (eintrag.art === "position") {
      return [{ ...eintrag.kopf, id: newInvoiceLine().id }];
    }
    return [
      { ...eintrag.kopf, id: newInvoiceLine().id, line_kind: BLOCK_KIND },
      ...eintrag.zeilen.map((line) => ({ ...line, id: newInvoiceLine().id })),
    ];
  });
}

/** Wie viele Belegzeilen ein Eintrag beim Einfügen erzeugt. */
export function eintragZeilenzahl(eintrag: AblageEintrag): number {
  return eintrag.art === "block" ? 1 + eintrag.zeilen.length : 1;
}

/** Kurze Herkunftsangabe für das Panel: „AN0231" oder „Angebot (Entwurf)". */
export function quelleBeschriften(kind: string, nummer: string): string {
  const art = kind === "quote" ? "Angebot" : "Rechnung";
  return nummer.trim() ? nummer.trim() : `${art} (Entwurf)`;
}
